import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Store } from '@reduxjs/toolkit'
import { Provider as StoreProvider } from 'react-redux'
import { Routes, Route, BrowserRouter } from 'react-router-dom'
import { ModalProvider } from 'view/shared/modal'
import { ShellOpenFin, Main, Tearout } from 'view/shell'
import { SelectionActions } from './store/selection'
import { ErrorBoundary } from 'view/shared/error/ErrorBoundary'
import { createStore } from 'createStore'
import { LoaderOverlay } from 'view/components/loader/Loader'
import { getConfig } from 'configurationLoader'
import { getWindowTitle } from 'utils'
import { WindowActions } from 'epics/windowEpic'
import { NotificationActionEvent, addEventListener, removeEventListener } from 'openfin-notifications'
import { EVENT_NOTIFICATION_CLICKED } from 'services/notificationService'
import { NavigateEventListener } from 'NavigateEventListener'
import { APPLICATION_NAME, QUOTE_APP_BASE_URL } from './constants'

export const AppOpenFin = () => {
  const { isLoading, windowTitle, store } = useConfigureOpenfinWindow()
  useNotificationClickedListener(store)

  if (isLoading) {
    return <LoaderOverlay />
  } else {
    return (
      <StoreProvider store={store}>
        <ErrorBoundary>
          <ModalProvider>
            <BrowserRouter basename={QUOTE_APP_BASE_URL}>
              <NavigateEventListener />
              <ShellOpenFin title={windowTitle}>
                <Routes>
                  <Route path="/tearout/*" element={<Tearout />} />
                  <Route path="*" element={<Main />} />
                </Routes>
              </ShellOpenFin>
            </BrowserRouter>
          </ModalProvider>
        </ErrorBoundary>
      </StoreProvider>
    )
  }
}

const useConfigureOpenfinWindow = () => {
  const configRef = useRef(getConfig())
  const [isLoading, setIsLoading] = useState(true)
  const [windowTitle, setWindowTitle] = useState(() => getWindowTitle(APPLICATION_NAME, configRef.current.environmentName))
  const store = useMemo<Store>(() => {
    if (window.opener) {
      return window.opener.__store
    } else {
      window.__store = createStore(configRef.current)
      return window.__store
    }
  }, [])

  useLayoutEffect(() => {
    if (!isLoading) return

    const currentWindow = fin.desktop.Window.getCurrent()

    currentWindow.getOptions(options => {
      const customData = options.customData || {}
      store.dispatch(SelectionActions.initialiseWindow(customData.windowSelections))
      customData.windowTitle && setWindowTitle(customData.windowTitle)
      setIsLoading(false)
    })
  }, [isLoading, store])

  return { isLoading, store, windowTitle }
}

const useNotificationClickedListener = (store: Store) => {
  useEffect(() => {
    const type = 'notification-action'
    const handler = (event: NotificationActionEvent) => {
      const {
        result: { customData, task }
      } = event
      if (task === EVENT_NOTIFICATION_CLICKED) {
        store.dispatch(WindowActions.navigateTo(customData))
      }
    }

    addEventListener(type, handler)

    return () => {
      removeEventListener(type, handler)
    }
  }, [store])
}
